import { useMemo } from 'react';
import {
  TERMS,
  bandCounts,
  bandFor,
  bandValue,
  type Assessment,
  type Measure,
  type Score,
  type Student,
  type Term,
} from '@td/shared';

/**
 * Class-level charts, drawn by hand in SVG.
 *
 * A donut and some bars are a few dozen lines of geometry; a charting library
 * would be a hundred kilobytes to draw them, on a page a teacher opens on
 * school wifi.
 */
export function ClassCharts({
  assessment,
  measure,
  students,
  scores,
  term,
}: {
  assessment: Assessment;
  measure: Measure;
  students: Student[];
  scores: Score[];
  term: Term;
}) {
  const forMeasure = useMemo(
    () => scores.filter((s) => s.measureId === measure.id),
    [scores, measure.id],
  );

  const thisTerm = useMemo(() => forMeasure.filter((s) => s.term === term), [forMeasure, term]);

  const counts = bandCounts(thisTerm, measure);
  const scored = counts.reduce((n, c) => n + c.count, 0);
  const missing = students.length - scored;

  const byTerm = useMemo(
    () =>
      TERMS.map((t) => ({
        term: t,
        counts: bandCounts(
          forMeasure.filter((s) => s.term === t),
          measure,
        ),
      })),
    [forMeasure, measure],
  );

  const values = thisTerm.map((s) => s.value).filter((v): v is number => v != null);
  const average = values.length
    ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10
    : null;

  if (measure.bands.length === 0) {
    return (
      <div className="text-sm text-walnut">
        <p className="mb-2">
          <strong className="font-normal text-espresso">{measure.name}</strong> has no benchmark
          bands, so there is nothing to divide the class into.
        </p>
        <p className="text-stone">
          Add bands under Edit measures — a cut score for each level — and the charts fill in.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section>
        <h3 className="mb-1 text-sm text-espresso">{term} — where the class sits</h3>
        <p className="mb-3 text-[11px] text-stone">
          {scored} of {students.length} scored
          {missing > 0 ? ` · ${missing} not assessed` : ''}
          {average != null ? ` · average ${average}` : ''}
        </p>

        <div className="flex items-center gap-5">
          <Donut counts={counts} total={scored} />

          <ul className="min-w-0 flex-1 space-y-1.5">
            {counts.map(({ band, count }) => (
              <li key={band.label} className="flex items-center gap-2 text-xs">
                <span
                  aria-hidden
                  className="h-3 w-3 shrink-0 rounded-sm"
                  style={{ backgroundColor: band.color }}
                />
                <span className="min-w-0 flex-1 truncate text-walnut">{band.label}</span>
                <span className="text-espresso">{count}</span>
                <span className="w-10 text-right text-stone">
                  {scored ? Math.round((count / scored) * 100) : 0}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h3 className="mb-1 text-sm text-espresso">Across the year</h3>
        <p className="mb-3 text-[11px] text-stone">
          How the class moves between bands from BOY to EOY.
        </p>
        <StackedBars byTerm={byTerm} total={students.length} />
      </section>

      <p className="text-[11px] text-stone lg:col-span-2 print:hidden">
        {assessment.name} · {measure.name}. Children with no score are left out of the percentages
        rather than counted as the lowest band.
      </p>
    </div>
  );
}

/** Band distribution as a ring, with the class size in the middle. */
function Donut({
  counts,
  total,
}: {
  counts: { band: { label: string; color: string }; count: number }[];
  total: number;
}) {
  const size = 132;
  const r = 52;
  const stroke = 22;
  const circumference = 2 * Math.PI * r;

  let offset = 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0 print-exact"
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EFE8DE" strokeWidth={stroke} />

      {total > 0 &&
        counts.map(({ band, count }) => {
          if (count === 0) return null;
          const portion = count / total;
          const dash = portion * circumference;
          const el = (
            <circle
              key={band.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={band.color}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              // Start at twelve o'clock rather than three, which is where a
              // reader expects a pie to begin.
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            >
              <title>{`${band.label}: ${count}`}</title>
            </circle>
          );
          offset += dash;
          return el;
        })}

      <text x={size / 2} y={size / 2 - 2} textAnchor="middle" fontSize={22} fill="#4A3F35">
        {total}
      </text>
      <text x={size / 2} y={size / 2 + 15} textAnchor="middle" fontSize={10} fill="#9C9086">
        scored
      </text>
    </svg>
  );
}

/** One stacked bar per term, so movement between bands is visible at a glance. */
function StackedBars({
  byTerm,
  total,
}: {
  byTerm: { term: Term; counts: { band: { label: string; color: string }; count: number }[] }[];
  total: number;
}) {
  const W = 320;
  const H = 150;
  const barH = 26;
  const gap = 18;
  const labelW = 38;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-sm print-exact">
      {byTerm.map((row, i) => {
        const scored = row.counts.reduce((n, c) => n + c.count, 0);
        const y = i * (barH + gap) + 8;
        let x = labelW;

        return (
          <g key={row.term}>
            <text x={0} y={y + barH / 2 + 4} fontSize={11} fill="#6B5D52">
              {row.term}
            </text>

            <rect x={labelW} y={y} width={W - labelW} height={barH} rx={4} fill="#EFE8DE" />

            {scored > 0 &&
              row.counts.map(({ band, count }) => {
                if (count === 0) return null;
                const w = (count / Math.max(scored, total)) * (W - labelW);
                const el = (
                  <rect key={band.label} x={x} y={y} width={w} height={barH} fill={band.color}>
                    <title>{`${row.term} · ${band.label}: ${count}`}</title>
                  </rect>
                );
                x += w;
                return el;
              })}

            {scored === 0 && (
              <text x={labelW + 8} y={y + barH / 2 + 4} fontSize={10} fill="#9C9086">
                nothing recorded
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Exported for the growth view's summary line. */
export function bandOf(score: Score | undefined, measure: Measure) {
  return bandFor(bandValue(score, measure), measure.bands);
}
